import React, { useState, useEffect } from "react";
import { Modal, Button, Header, Input, Image, Loader, Dimmer } from "semantic-ui-react";
import "./ArticleWeightModal.css";
import NumPad from "../NumPad/NumPad";
import * as Constants from "../../utils/Constants";
import SharedFunctions from "../../utils/SharedFunctions";
import SkuEndpoints from "../../lib/sku/SkuEndpoints";
import useInterval from "use-interval";
import { IHttpError } from "../../lib/IHttpError";
import { IExtendedSortBoxItemModel } from "../../lib/SortBoxItem/IExtendedSortBoxItemModel";
import { ARTICLE_WEIGHT_MODAL } from "../../utils/constants/componentConstants";

const { KeyPadKeyCode } = Constants;

interface IArticleWeightModalProps {
  boxItems?: Array<IExtendedSortBoxItemModel>;
  getWeight: (skuID?: number) => Promise<any> | any;
  setWeight: (skuID?: number, weight?: number) => Promise<any> | any;
  cartShelfID?: string;
  callback: (skuID?: number) => void;
  autoScaleEnabled: boolean;
  scannedSKU: number;
  weightError:string | null;
}

const ArticleWeightModal = ({
  boxItems,
  getWeight,
  setWeight,
  cartShelfID,
  callback,
  autoScaleEnabled = false,
  scannedSKU,
  weightError =""
}: IArticleWeightModalProps) => {
  const items = boxItems || [];
  const index = items.findIndex((bi) => bi.requireWeighing && scannedSKU === bi.skuID);
  const leftToWeighCount = index === -1 ? 0 : 1;

  const currentBoxItem = items[index] || {};
  const isRXBag =
    currentBoxItem.eans && currentBoxItem.eans[0].substring(0, 4) === "3990" ? true : false;
  if (!currentBoxItem.eans || currentBoxItem.eans[0] === "") {
    autoScaleEnabled = false;
  }
  const [weight, setWeightState] = useState(0);
  const [weightConfirm, setWeightConfirmState] = useState(0);
  const [isvisibileconfirm, setVisibleconfirm] = useState(false);
  const [error, setError] = useState(weightError);
  const [showLoading, setShowLoading] = useState(false);
  const [initialRender, setInitialRender] = useState(false);
  const [weightMax, setWeightMax] = useState(0);
  const [weightMin, setWeightMin] = useState(0);
  const [openModal, setOpenModal] = useState(false);
  const [pollWeightForSku, setPollWeightForSku] = useState(false);
  const [pollWeightForSkuPaused, setPollWeightForSkuPaused] = useState(false);

  if (openModal && leftToWeighCount > 0) {
    SharedFunctions.LogPopupShownEvent(ARTICLE_WEIGHT_MODAL);
  }

  const checkWeightForSku = (skuID?: number): void => {
    if ((skuID && skuID === -1) || pollWeightForSkuPaused) return;

    SkuEndpoints.checkWeightForSku(skuID)
      .then((result: { weight: number }) => {
        if (result.weight > 0) {
          clearState();
          if (callback) {
            callback(skuID);
          }
        }
      })
      .catch((err: IHttpError) => {
        console.log(`error in checkWeightForSku ${err.exceptionMessage}`);
      });
  };

  useInterval(
    () => {
      if (leftToWeighCount > 0) checkWeightForSku(currentBoxItem.skuID);
    },
    pollWeightForSku ? 5000 : null
  );

  useEffect(() => {
    if (currentBoxItem.skuID && !isRXBag) {
      getWeight(currentBoxItem.skuID)
        .then((x: { max: number; min: number }) => {
          setWeightMax(x.max);
          setWeightMin(x.min);
          setOpenModal(!!leftToWeighCount);
        })
        .then(setPollWeightForSku(true));
    }
  }, [currentBoxItem.skuID, getWeight, isRXBag, leftToWeighCount]);

  const numberChange = (value: string, setcurrentState: (value: number) => void): void => {
    let parsedValue = parseInt(value);
    if (parsedValue || parsedValue === 0) {
      if (parsedValue < 0) {
        parsedValue = 0;
      }
      setcurrentState(parsedValue);
    } else {
      setcurrentState(0);
    }
  };

  const clearState = (): void => {
    setPollWeightForSku(false);
    setError("");
    setWeightState(0);
    setWeightConfirmState(0);
    setShowLoading(false);
    setInitialRender(true);
    setOpenModal(false);
  };

  const setWeightCheck = (): void => {
    setInitialRender(false);
    if (weight) {
      setShowLoading(true);
      setWeight(currentBoxItem.skuID, weight)
        .then(clearState)
        .then(() => {
          if (callback) {
            callback();
          }
        })
        .catch(() => {
          clearState();
          if (callback) {
            callback();
          }
          setError("Fel vid kommunikation med server");
        });
    } else {
      setError("Vikt saknas!");
    }
  };

  const confirmWeight = (): void => {
    setInitialRender(false);
    if (weightConfirm === weight) {
      // setError("Felaktig vikt, måste vara under 20kg!")
      setShowLoading(true);
      setWeight(currentBoxItem.skuID, weightConfirm)
        .then(clearState)
        .then(setVisibleconfirm(false))
        .catch(() => {
          clearState();
          setError("Fel vid kommunikation med server");
        });
    } else if (weightConfirm) {
      setError("Vikten matchar inte");
    } else {
      setError("Bekräfta vikt nedan");
    }
  };

  const onKeyPadInut = (key: string): void => {
    if (key === KeyPadKeyCode.ENTER && weight) {
      setWeightCheck();
    } else if (key === KeyPadKeyCode.BACKSPACE) {
      const weightString = weight.toString();
      numberChange(weightString.substr(0, weightString.length - 1), setWeightState);
    } else if ((key || key === KeyPadKeyCode.ZERO) && key !== KeyPadKeyCode.BACKSPACE) {
      numberChange(weight + key, setWeightState);
    }
  };

  const onKeyPadInutConfirm = (key: string): void => {
    if (key === KeyPadKeyCode.ENTER) {
      confirmWeight();
    } else if (key === KeyPadKeyCode.BACKSPACE) {
      const weightString = weightConfirm.toString();
      numberChange(weightString.substr(0, weightString.length - 1), setWeightConfirmState);
    } else if ((key || key === KeyPadKeyCode.ZERO) && key !== KeyPadKeyCode.BACKSPACE) {
      numberChange(weightConfirm + key, setWeightConfirmState);
    }
  };

  const returnToWeight = (): void => {
    setVisibleconfirm(false);
    setError("");
    setWeightState(0);
    setWeightConfirmState(0);
    setInitialRender(true);
  };

  const closeConfirmationModal = (): void => {
    setPollWeightForSkuPaused(false);
  };

  if (leftToWeighCount === 0) return null;

  return (
    <>
      {leftToWeighCount > 0 && !pollWeightForSkuPaused && (
        <Modal
          open={openModal}
          size="small"
          className={"ArticleWeightModal"}
          closeOnDimmerClick={false}
          closeOnDocumentClick={false}
          closeOnEscape={false}
        >
          <Header icon="warning sign" color="red" content="Artikel behöver vägas!" />
          <Modal.Content className={initialRender ? "fade-in" : ""}>
            <Dimmer active={showLoading} inverted>
              <Loader size="big" />
            </Dimmer>
            <h3>{currentBoxItem.name}</h3>
            <h4>Sku: {currentBoxItem.skuID}</h4>
            <h4>EAN: {currentBoxItem.eans && currentBoxItem.eans[0]}</h4>
            {cartShelfID ? <div className="shelf-id-info">Fack: {cartShelfID}</div> : null}
            <Image className="article-image" src={currentBoxItem.imageURL} />
            {autoScaleEnabled ? (
              <Header color="red" content="Väg artikel på automatvågen" />
            ) : (
              <Input type="number" value={weight} placeholder={"Vikt i gram"} disabled /> 
            )} <span>Vikt (gram)</span>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
              }}
              className="error-message"
            >
              {(error != "")? error : weightError}
              <Button
                style={{
                  display: isvisibileconfirm ? "block" : "none",
                  width: "120px",
                  margin: "0 auto",
                }}
                onClick={() => returnToWeight()}
              >
                Ångra
              </Button>
            </div>
            {autoScaleEnabled ? null : (
              <Input
                type="number"
                value={weightConfirm}
                placeholder={"Vikt i gram"}
                disabled
                style={{ display: isvisibileconfirm ? "block" : "none" }}
              />
            )}
          </Modal.Content>

          <Modal.Actions>
            {autoScaleEnabled ? null : (
              <NumPad
                onClick={(val: string) =>
                  isvisibileconfirm ? onKeyPadInutConfirm(val) : onKeyPadInut(val)
                }
                className={""}
              />
            )}
          </Modal.Actions>
        </Modal>
      )}
      {pollWeightForSkuPaused && (
        <Modal
          open={true}
          size="small"
          closeOnDimmerClick={false}
          closeOnDocumentClick={false}
          closeOnEscape={false}
        >
          <Modal.Content>
            <h2>Vikt redan angiven vid annan station</h2>
          </Modal.Content>
          <Modal.Actions>
            <Button primary onClick={closeConfirmationModal}>
              Okej
            </Button>
          </Modal.Actions>
        </Modal>
      )}
    </>
  );
};

export default ArticleWeightModal;
